import { useState } from 'react'
import clsx from 'clsx'
import useTranslation from 'next-translate/useTranslation'
import ClockCircleOutlineIcon from '@ancon/wildcat-ui/shared/icons/clock-circle-outline.svg'
import BodyText from '@ancon/wildcat-ui/web/orderweb/BodyText'
import { OutletListItem, OutletState } from '@ancon/wildcat-types'
import getOutletCurrentClosingTimeText from '@ancon/wildcat-utils/outlet/getOutletCurrentClosingTimeText'
import getOutletNextOpeningTimeText from '@ancon/wildcat-utils/outlet/getOutletNextOpeningTimeText'
import getListOutletOpeningHoursTable from '@ancon/wildcat-utils/outlet/getListOutletOpeningHoursTable'
import Button from '@ancon/wildcat-ui/web/orderweb/Button'

import Accordion from '../../../app/components/Accordion'
import useGetOrderFormatName from '../../../app/hooks/useGetOrderFormatName'
import isOutletAvailableForOrders from '../../utils/isOutletAvailableForOrders'
import getOutletStatusLabel from '../../utils/getOutletStatusLabel'

import OutletOpeningHoursSet from './OutletOpeningHoursSet'
import styles from './OutletDetailsOpeningHours.module.scss'

type OpeningHoursTableProps = {
  outletListItem: OutletListItem
}

function OpeningHoursTable({ outletListItem }: OpeningHoursTableProps) {
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0)

  const getOrderFormatName = useGetOrderFormatName()

  const openingHoursTable = getListOutletOpeningHoursTable(outletListItem)

  return (
    <div className={styles.openingHoursContainer}>
      <div className={styles.openingHoursGroups}>
        {openingHoursTable.map(({ orderFormats }, index) => {
          const orderFormatIds = orderFormats.map(({ id }) =>
            getOrderFormatName(id),
          )
          const sectionKey = orderFormatIds.join('_')
          const sectionTitle = orderFormatIds.join(' / ')

          return (
            <Button
              key={sectionKey}
              variant="secondary"
              onClick={() => setSelectedSectionIndex(index)}
              className={clsx({
                [styles.selectedOpeningHourOrderFormat]:
                  selectedSectionIndex === index,
              })}
            >
              {sectionTitle}
            </Button>
          )
        })}
      </div>
      <OutletOpeningHoursSet item={openingHoursTable[selectedSectionIndex]} />
    </div>
  )
}

type OutletDetailsOpeningHoursProps = {
  outletListItem: OutletListItem
}

export default function OutletDetailsOpeningHours({
  outletListItem,
}: OutletDetailsOpeningHoursProps) {
  const { t } = useTranslation('common')

  const isOpened = outletListItem.state === OutletState.Opened

  const isAvailableForOrders = isOutletAvailableForOrders(outletListItem)

  function outletDetailsModalTranslateFn(
    key: string,
    props?: Record<string, string>,
  ) {
    return t(`outletDetailsModal.${key}`, props)
  }

  const outletOpeningTime = isOpened
    ? getOutletCurrentClosingTimeText(
        outletListItem,
        outletDetailsModalTranslateFn,
      )
    : getOutletNextOpeningTimeText(
        outletListItem,
        outletDetailsModalTranslateFn,
      )

  const outletStatusLabel = getOutletStatusLabel(outletListItem, t)

  return (
    <Accordion
      caretClassName={styles.caret}
      title={
        <div className={styles.headerContainer}>
          <ClockCircleOutlineIcon />
          <div className={styles.outletStatusContainer}>
            <BodyText
              className={clsx(styles.outletStatus, {
                [styles.outletStatusOpened]: isAvailableForOrders,
                [styles.outletStateClosed]: !isAvailableForOrders,
              })}
            >
              {outletStatusLabel}
            </BodyText>
            <BodyText color="body-1" className={styles.outletOpeningHour}>
              {outletOpeningTime}
            </BodyText>
          </div>
        </div>
      }
    >
      <OpeningHoursTable outletListItem={outletListItem} />
    </Accordion>
  )
}
